/**
 * Runtime tenant detection and theme application.
 *
 * Detects the tenant from the hostname (subdomain) or a `tenant` query parameter,
 * then sets a `data-tenant` attribute on the root <html> element.
 *
 * CSS custom property overrides in src/themes/tenant-*.css are activated
 * by the [data-tenant="<name>"] selector.
 *
 * Call this once at app startup (e.g., in index.tsx or App.tsx).
 */

const KNOWN_TENANTS = ["client1"] as const;
type Tenant = (typeof KNOWN_TENANTS)[number];

function detectTenant(): Tenant | null {
  // 1. Check query parameter (?tenant=client1) — useful for development/testing
  const params = new URLSearchParams(window.location.search);
  const paramTenant = params.get("tenant");
  if (paramTenant && KNOWN_TENANTS.includes(paramTenant as Tenant)) {
    return paramTenant as Tenant;
  }

  // 2. Check subdomain (e.g., client1.app.planomatic.com)
  const subdomain = window.location.hostname.split(".")[0];
  if (KNOWN_TENANTS.includes(subdomain as Tenant)) {
    return subdomain as Tenant;
  }

  return null;
}

export function applyTenantTheme(): void {
  const tenant = detectTenant();
  if (tenant) {
    document.documentElement.setAttribute("data-tenant", tenant);
  }
}
